import {
  IonPage,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonAlert,
  IonToggle,
  useIonToast,
  useIonRouter,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useApolloClient } from '@apollo/client/react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { GENERATE_CHANGE_PASSWORD_URL } from '../../../graphql/mutations/generateChangePasswordUrl';
import { DELETE_USER_ACCOUNT } from '../../../graphql/mutations/deleteUserAccount';
import { useState } from 'react';
import { performLogout, getAuthRedirectUri } from '../../../utils/auth';
import {
  cleanupPushNotificationsAfterAccountDeletion,
  unregisterPushNotifications,
  registerPushNotifications,
  disablePushNotifications,
} from '../../../utils/pushNotifications';
import { LegalContentModal } from '../../components/LegalContentModal';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';

export const SettingsPage = () => {
  const { logout } = useAuth0();
  const apolloClient = useApolloClient();
  const [presentToast] = useIonToast();
  const ionRouter = useIonRouter();
  const [generateChangePasswordUrl, { loading: changingPassword }] = useMutation(GENERATE_CHANGE_PASSWORD_URL);
  const [deleteUserAccount] = useMutation(DELETE_USER_ACCOUNT);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => localStorage.getItem('pushNotificationsDisabled') !== 'true'
  );

  const isMobile = Capacitor.isNativePlatform();

  const showErrorToast = (message: string) => {
    presentToast({
      message,
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'app-footer',
      cssClass: 'toast-danger',
      buttons: [{ icon: closeOutline, role: 'cancel' }],
    });
  };

  const handleLogout = async () => {
    const storedToken = localStorage.getItem('pushNotificationToken');
    if (storedToken) {
      try {
        await unregisterPushNotifications(apolloClient, storedToken);
      } catch (error) {
        console.error('Failed to unregister device, but continuing logout:', error);
      }
      localStorage.removeItem('pushNotificationToken');
    }

    await apolloClient.clearStore();

    const logoutUri = getAuthRedirectUri(isMobile, 'landing');
    await performLogout(logout, { returnTo: logoutUri, isMobile });
  };

  const handleLogoutAfterAccountDeletion = async () => {
    await cleanupPushNotificationsAfterAccountDeletion();
    await apolloClient.clearStore();

    const logoutUri = getAuthRedirectUri(isMobile, 'landing');
    await performLogout(logout, { returnTo: logoutUri, isMobile });
  };

  const handleChangePassword = async () => {
    try {
      const result = await generateChangePasswordUrl();
      const data = result?.data as { generateChangePasswordUrl?: { url?: string | null; errors?: string[] } };

      if (data?.generateChangePasswordUrl?.errors && data.generateChangePasswordUrl.errors.length > 0) {
        showErrorToast(data.generateChangePasswordUrl.errors[0]);
        return;
      }

      const url = data?.generateChangePasswordUrl?.url;
      if (!url) {
        showErrorToast('No se pudo generar el enlace de cambio de contraseña');
        return;
      }

      if (isMobile) {
        await Browser.open({ url });
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('[ChangePassword] Error generating password change URL:', error);
      showErrorToast('Error al generar el enlace de cambio de contraseña');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const result = await deleteUserAccount();
      const data = result?.data as { deleteUserAccount?: { success: boolean; errors: string[] } };

      if (!data?.deleteUserAccount?.success) {
        const errors = data?.deleteUserAccount?.errors ?? [];
        showErrorToast(errors[0] ?? 'No se pudo eliminar la cuenta');
        return;
      }

      await handleLogoutAfterAccountDeletion();
    } catch (error) {
      console.error('[DeleteAccount] Error deleting account:', error);
      showErrorToast('Error al eliminar la cuenta');
    }
  };

  const handleNotificationsToggle = async () => {
    if (notificationsEnabled) {
      try {
        await disablePushNotifications(apolloClient);
        setNotificationsEnabled(false);
      } catch {
        showErrorToast('Error al desactivar las notificaciones');
      }
    } else {
      try {
        localStorage.removeItem('pushNotificationsDisabled');
        await registerPushNotifications({
          apolloClient,
          navigate: (path) => ionRouter.push(path, 'forward'),
        });
        setNotificationsEnabled(true);
      } catch {
        showErrorToast('Error al activar las notificaciones');
      }
    }
  };

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/perfil" />
          </IonButtons>
          <IonTitle className="ml-4">Configuración</IonTitle>
        </IonToolbar>
      </AppSectionHeader>

      <IonContent>
        <IonList>
          <IonItem button onClick={handleChangePassword} disabled={changingPassword}>
            <IonLabel>Cambiar contraseña</IonLabel>
            {changingPassword && <IonSpinner name="crescent" />}
          </IonItem>

          {isMobile && (
            <IonItem>
              <IonLabel>Notificaciones push</IonLabel>
              <IonToggle
                checked={notificationsEnabled}
                onIonChange={handleNotificationsToggle}
                slot="end"
              />
            </IonItem>
          )}

          <IonItem button onClick={() => setShowTermsModal(true)}>
            <IonLabel>Términos y Condiciones</IonLabel>
          </IonItem>

          <IonItem button onClick={() => setShowDeleteAlert(true)}>
            <IonLabel color="danger">Eliminar cuenta</IonLabel>
          </IonItem>

          <IonItem button onClick={handleLogout}>
            <IonLabel>Cerrar sesión</IonLabel>
          </IonItem>
        </IonList>
      </IonContent>

      <LegalContentModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        mode="view"
      />

      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header="Eliminar cuenta"
        message="¿Estás seguro de que quieres eliminar tu cuenta? Esta acción no se puede deshacer y perderás todos tus datos."
        buttons={[
          {
            text: 'Cancelar',
            role: 'cancel',
          },
          {
            text: 'Eliminar',
            role: 'destructive',
            handler: handleDeleteAccount,
          },
        ]}
      />
    </IonPage>
  );
};
